import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from '../../utils/axiosConfig';
import '../../scss/components/LessonsPage/LessonContent.scss';
import backIcon from '../../assets/back-btn.svg';
import lessonsData from '../../data/lessonsData.json';
import { fetchLastCompletedLesson } from '../../redux/slices/userSlice';

// Импортируем компоненты для содержимого урока
import LessonDescription from './LessonContent/LessonDescription';
import LessonHomework from './LessonContent/LessonHomework';

const LessonContent = () => {
    const params = useParams();
    const { moduleName, lessonId } = params;
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('description');
    const [previousTab, setPreviousTab] = useState(null);
    const [tabAnimating, setTabAnimating] = useState(false);
    const [lessonTitle, setLessonTitle] = useState('Урок');
    const [previousTitle, setPreviousTitle] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [isEditingUrl, setIsEditingUrl] = useState(false);
    const [newVideoUrl, setNewVideoUrl] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const videoInputRef = useRef(null);
    const [currentModuleLessons, setCurrentModuleLessons] = useState([]);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

    // Состояния для анимации переходов между уроками
    const [animating, setAnimating] = useState(false);
    const [slideDirection, setSlideDirection] = useState(''); // 'enter' или 'exit'
    const [, setNextLessonData] = useState(null);
    const [counterAnimation, setCounterAnimation] = useState(''); // 'up' или 'down'
    const [prevCounterValue, setPrevCounterValue] = useState(null);
    const [nextCounterValue, setNextCounterValue] = useState(null);
    const animationTimer = useRef(null);
    const tabAnimationTimer = useRef(null);

    // Состояния для анимации заголовка
    const [titleAnimating, setTitleAnimating] = useState(false);
    const [titleSlideDirection, setTitleSlideDirection] = useState('');

    // Получаем статус админ-режима, isAccepted и данные о последнем завершённом уроке из Redux
    const adminMode = useSelector((state) => state.adminMode.adminMode);
    const { isAccepted, lastCompletedModule, lastCompletedLessonId } = useSelector(state => state.user);
    const { lessonAccess } = useSelector(state => state.lessonAccess);

    // Загружаем информацию о последнем завершённом уроке при монтировании компонента
    useEffect(() => {
        dispatch(fetchLastCompletedLesson());
    }, [dispatch]);

    // Проверка доступа к уроку при загрузке компонента
    useEffect(() => {
        // Получаем информацию о доступе из state навигации
        const lessonAccessInfo = location.state?.lessonAccess;

        // Если нет информации о доступе, проверяем через логику из LessonsList
        if (!lessonAccessInfo) {
            // Проверяем доступ через логику из LessonsList
            const moduleIdNum = Number(moduleName);
            const lessonIdNum = Number(lessonId);

            // Проверка на isAccepted
            if (!isAccepted && (
                (moduleIdNum === 1 && lessonIdNum > 101) ||
                moduleIdNum > 1
            )) {
                navigate('/');
                return;
            }

            // Проверка последовательности уроков
            if (moduleIdNum === lastCompletedModule) {
                if (lessonIdNum > lastCompletedLessonId + 1) {
                    navigate('/');
                    return;
                }
            } else if (moduleIdNum > lastCompletedModule + 1) {
                navigate('/');
                return;
            }
        } else {
            // Используем информацию из state навигации
            if (!lessonAccessInfo.canAccess) {
                navigate('/');
                return;
            }
        }
    }, [moduleName, lessonId, isAccepted, lastCompletedModule, lastCompletedLessonId, navigate, location.state]);

    // Функция для получения ссылки на видео
    const fetchVideoUrl = useCallback(async () => {
        try {
            const response = await axiosInstance.get(`/lesson-videos/${moduleName}/${lessonId}`);
            setVideoUrl(response.data.videoUrl);
            setNewVideoUrl(response.data.videoUrl);
        } catch (error) {
            setErrorMessage('Не удалось загрузить видео');
        }
    }, [moduleName, lessonId]);

    // Получаем ссылку на видео при загрузке компонента и при изменении урока
    useEffect(() => {
        // Сбрасываем URL видео при смене урока, чтобы принудительно перерендерить
        setVideoUrl('');
        setNewVideoUrl('');
        fetchVideoUrl();
    }, [fetchVideoUrl, moduleName, lessonId]);

    // Загружаем данные о модуле и уроках
    useEffect(() => {
        // Находим текущий модуль
        const moduleIdNum = Number(moduleName);
        const currentModule = lessonsData.modules.find(module => module.id === moduleIdNum);

        if (currentModule && currentModule.lessons) {
            setCurrentModuleLessons(currentModule.lessons);

            // Находим индекс текущего урока
            const lessonIdNum = Number(lessonId);
            const index = currentModule.lessons.findIndex(lesson => lesson.id === lessonIdNum);
            if (index !== -1) {
                setCurrentLessonIndex(index);
            }
        }
    }, [moduleName, lessonId]);

    // Очистка таймеров анимации при размонтировании компонента
    useEffect(() => {
        return () => {
            if (animationTimer.current) {
                clearTimeout(animationTimer.current);
            }
            if (tabAnimationTimer.current) {
                clearTimeout(tabAnimationTimer.current);
            }
        };
    }, []);

    // Функция для переключения вкладок с анимацией (без анимации заголовка)
    const handleTabChange = (newTab) => {
        if (newTab === activeTab || tabAnimating) return;

        setPreviousTab(activeTab);
        setTabAnimating(true);

        // Анимация выхода текущей вкладки
        setSlideDirection('exit');

        // После анимации выхода переключаем вкладку и запускаем анимацию входа
        tabAnimationTimer.current = setTimeout(() => {
            setActiveTab(newTab);
            setSlideDirection('enter');

            // Сбрасываем состояние анимации после завершения
            tabAnimationTimer.current = setTimeout(() => {
                setTabAnimating(false);
                setSlideDirection('');
            }, 500);
        }, 500);
    };

    // Функция для перехода к предыдущему уроку с анимацией
    const goToPreviousLesson = () => {
        if (canNavigateToPreviousLesson() && !animating) {
            const prevLesson = currentModuleLessons[currentLessonIndex - 1];

            // Администраторам доступны все уроки
            if (adminMode) {
                // Сохраняем текущий заголовок для анимации
                setPreviousTitle(lessonTitle);

                // Сохраняем значения счетчика для анимации
                setPrevCounterValue(currentLessonIndex + 1);
                setNextCounterValue(currentLessonIndex); // На 1 меньше текущего

                // Устанавливаем состояние анимации
                setAnimating(true);
                setSlideDirection('exit');
                setTitleAnimating(true);
                setTitleSlideDirection('exit');
                setCounterAnimation('down'); // Меняем на 'down' - число уезжает вниз при "Назад"
                setNextLessonData({
                    moduleId: moduleName,
                    lessonId: prevLesson.id,
                    direction: 'prev'
                });

                // После анимации выхода переходим к предыдущему уроку
                animationTimer.current = setTimeout(() => {
                    navigate(`/lessons/${moduleName}/${prevLesson.id}`, {
                        state: {
                            lessonAccess: {
                                isLocked: false,
                                lockReason: '',
                                canAccess: true
                            }
                        }
                    });
                    setSlideDirection('enter');
                    setTitleSlideDirection('enter');

                    // Сбрасываем состояние анимации после завершения
                    animationTimer.current = setTimeout(() => {
                        setAnimating(false);
                        setTitleAnimating(false);
                        setNextLessonData(null);
                        setSlideDirection('');
                        setTitleSlideDirection('');
                        setCounterAnimation('');
                        setPrevCounterValue(null);
                        setNextCounterValue(null);
                    }, 500);
                }, 500);
                return;
            }

            // Проверяем доступ к предыдущему уроку через логику из LessonCard
            const moduleIdNum = Number(moduleName);
            const prevLessonIdNum = Number(prevLesson.id);

            // Проверяем блокировку из БД
            const accessInfo = lessonAccess.find(
                access => access.moduleId === moduleIdNum && access.lessonId === prevLessonIdNum
            );
            const isBlockedByAdmin = accessInfo && accessInfo.isLocked;

            // Проверка на isAccepted
            const isBlockedByAcceptance = !isAccepted && (
                (moduleIdNum === 1 && prevLessonIdNum > 101) ||
                moduleIdNum > 1
            );

            // Проверка последовательности уроков
            let isBlockedByCompletion = false;
            if (moduleIdNum === lastCompletedModule) {
                if (prevLessonIdNum > lastCompletedLessonId + 1) {
                    isBlockedByCompletion = true;
                }
            } else if (moduleIdNum > lastCompletedModule + 1) {
                isBlockedByCompletion = true;
            }

            const canAccess = !isBlockedByAdmin && !isBlockedByAcceptance && !isBlockedByCompletion;
            const lockReason = isBlockedByAdmin ? 'locked' : isBlockedByAcceptance ? 'acceptance' : isBlockedByCompletion ? 'completion' : '';

            // Сохраняем текущий заголовок для анимации
            setPreviousTitle(lessonTitle);

            // Сохраняем значения счетчика для анимации
            setPrevCounterValue(currentLessonIndex + 1);
            setNextCounterValue(currentLessonIndex); // На 1 меньше текущего

            // Устанавливаем состояние анимации
            setAnimating(true);
            setSlideDirection('exit');
            setTitleAnimating(true);
            setTitleSlideDirection('exit');
            setCounterAnimation('down'); // Меняем на 'down' - число уезжает вниз при "Назад"
            setNextLessonData({
                moduleId: moduleName,
                lessonId: prevLesson.id,
                direction: 'prev'
            });

            // После анимации выхода переходим к предыдущему уроку
            animationTimer.current = setTimeout(() => {
                navigate(`/lessons/${moduleName}/${prevLesson.id}`, {
                    state: {
                        lessonAccess: {
                            isLocked: !canAccess,
                            lockReason: lockReason,
                            canAccess: canAccess
                        }
                    }
                });
                setSlideDirection('enter');
                setTitleSlideDirection('enter');

                // Сбрасываем состояние анимации после завершения
                animationTimer.current = setTimeout(() => {
                    setAnimating(false);
                    setTitleAnimating(false);
                    setNextLessonData(null);
                    setSlideDirection('');
                    setTitleSlideDirection('');
                    setCounterAnimation('');
                    setPrevCounterValue(null);
                    setNextCounterValue(null);
                }, 500);
            }, 500);
        }
    };

    // Функция для перехода к следующему уроку с анимацией
    const goToNextLesson = () => {
        // Проверяем, можно ли перейти к следующему уроку
        if (canNavigateToNextLesson() && !animating) {
            const nextLesson = currentModuleLessons[currentLessonIndex + 1];

            // Администраторам доступны все уроки
            if (adminMode) {
                // Сохраняем текущий заголовок для анимации
                setPreviousTitle(lessonTitle);

                // Сохраняем значения счетчика для анимации
                setPrevCounterValue(currentLessonIndex + 1);
                setNextCounterValue(currentLessonIndex + 2); // На 1 больше текущего

                // Устанавливаем состояние анимации
                setAnimating(true);
                setSlideDirection('exit');
                setTitleAnimating(true);
                setTitleSlideDirection('exit');
                setCounterAnimation('up'); // Меняем на 'up' - число уезжает вверх при "Далее"
                setNextLessonData({
                    moduleId: moduleName,
                    lessonId: nextLesson.id,
                    direction: 'next'
                });

                // После анимации выхода переходим к следующему уроку
                animationTimer.current = setTimeout(() => {
                    navigate(`/lessons/${moduleName}/${nextLesson.id}`, {
                        state: {
                            lessonAccess: {
                                isLocked: false,
                                lockReason: '',
                                canAccess: true
                            }
                        }
                    });
                    setSlideDirection('enter');
                    setTitleSlideDirection('enter');

                    // Сбрасываем состояние анимации после завершения
                    animationTimer.current = setTimeout(() => {
                        setAnimating(false);
                        setTitleAnimating(false);
                        setNextLessonData(null);
                        setSlideDirection('');
                        setTitleSlideDirection('');
                        setCounterAnimation('');
                        setPrevCounterValue(null);
                        setNextCounterValue(null);
                    }, 500);
                }, 500);
                return;
            }

            // Проверяем доступ к следующему уроку через логику из LessonCard
            const moduleIdNum = Number(moduleName);
            const nextLessonIdNum = Number(nextLesson.id);

            // Проверяем блокировку из БД
            const accessInfo = lessonAccess.find(
                access => access.moduleId === moduleIdNum && access.lessonId === nextLessonIdNum
            );
            const isBlockedByAdmin = accessInfo && accessInfo.isLocked;

            // Проверка на isAccepted
            const isBlockedByAcceptance = !isAccepted && (
                (moduleIdNum === 1 && nextLessonIdNum > 101) ||
                moduleIdNum > 1
            );

            // Проверка последовательности уроков
            let isBlockedByCompletion = false;
            if (moduleIdNum === lastCompletedModule) {
                if (nextLessonIdNum > lastCompletedLessonId + 1) {
                    isBlockedByCompletion = true;
                }
            } else if (moduleIdNum > lastCompletedModule + 1) {
                isBlockedByCompletion = true;
            }

            const canAccess = !isBlockedByAdmin && !isBlockedByAcceptance && !isBlockedByCompletion;
            const lockReason = isBlockedByAdmin ? 'locked' : isBlockedByAcceptance ? 'acceptance' : isBlockedByCompletion ? 'completion' : '';

            // Сохраняем текущий заголовок для анимации
            setPreviousTitle(lessonTitle);

            // Сохраняем значения счетчика для анимации
            setPrevCounterValue(currentLessonIndex + 1);
            setNextCounterValue(currentLessonIndex + 2); // На 1 больше текущего

            // Устанавливаем состояние анимации
            setAnimating(true);
            setSlideDirection('exit');
            setTitleAnimating(true);
            setTitleSlideDirection('exit');
            setCounterAnimation('up'); // Меняем на 'up' - число уезжает вверх при "Далее"
            setNextLessonData({
                moduleId: moduleName,
                lessonId: nextLesson.id,
                direction: 'next'
            });

            // После анимации выхода переходим к следующему уроку
            animationTimer.current = setTimeout(() => {
                navigate(`/lessons/${moduleName}/${nextLesson.id}`, {
                    state: {
                        lessonAccess: {
                            isLocked: !canAccess,
                            lockReason: lockReason,
                            canAccess: canAccess
                        }
                    }
                });
                setSlideDirection('enter');
                setTitleSlideDirection('enter');

                // Сбрасываем состояние анимации после завершения
                animationTimer.current = setTimeout(() => {
                    setAnimating(false);
                    setTitleAnimating(false);
                    setNextLessonData(null);
                    setSlideDirection('');
                    setTitleSlideDirection('');
                    setCounterAnimation('');
                    setPrevCounterValue(null);
                    setNextCounterValue(null);
                }, 500);
            }, 500);
        }
    };

    // Проверка возможности перехода к предыдущему уроку
    const canNavigateToPreviousLesson = () => {
        // Администраторам доступны все уроки
        if (adminMode) {
            return currentLessonIndex > 0;
        }
        return currentLessonIndex > 0;
    };

    // Получаем последний урок модуля
    const getLastLessonIdInModule = (moduleNum) => {
        if (moduleNum < 1) return 0;

        const module = lessonsData.modules.find(m => m.id === moduleNum);
        if (!module || !module.lessons || module.lessons.length === 0) return 0;

        return module.lessons[module.lessons.length - 1].id;
    };

    // Получаем причину блокировки кнопки "Далее"
    const getNextButtonStatus = () => {
        const moduleIdNum = Number(moduleName);
        const lessonIdNum = Number(lessonId);

        // Если это последний урок в модуле
        if (currentLessonIndex >= currentModuleLessons.length - 1) {
            return { disabled: true, message: 'Далее', reason: 'last_lesson' };
        }

        // Если пользователь админ, разрешаем всё
        if (adminMode) {
            return { disabled: false, message: 'Далее', reason: '' };
        }

        // Проверяем блокировку следующего урока из БД
        const nextLesson = currentModuleLessons[currentLessonIndex + 1];
        if (nextLesson) {
            const nextLessonIdNum = Number(nextLesson.id);
            const accessInfo = lessonAccess.find(
                access => access.moduleId === moduleIdNum && access.lessonId === nextLessonIdNum
            );

            if (accessInfo && accessInfo.isLocked) {
                return { disabled: true, message: 'Далее', reason: 'locked' };
            }
        }

        // Если пользователь не подтвержден (isAccepted = false)
        if (!isAccepted) {
            if ((moduleIdNum === 1 && currentLessonIndex > 0) || moduleIdNum > 1) {
                return { disabled: true, message: 'Далее', reason: 'acceptance' };
            }
        }

        // Для первого урока первого модуля всегда даём доступ
        if (moduleIdNum === 1 && lessonIdNum === 101) {
            return { disabled: false, message: 'Далее', reason: '' };
        }

        // Проверяем доступность урока на основе последнего завершённого урока
        if (moduleIdNum === lastCompletedModule) {
            // В текущем модуле - только следующий урок после последнего завершенного
            if (lessonIdNum <= lastCompletedLessonId || lessonIdNum === lastCompletedLessonId + 1) {
                return { disabled: false, message: 'Далее', reason: '' };
            }
            return { disabled: true, message: 'Далее', reason: 'completion' };
        }
        else if (moduleIdNum < lastCompletedModule) {
            // В предыдущих модулях - все уроки доступны
            return { disabled: false, message: 'Далее', reason: '' };
        }
        else if (moduleIdNum === lastCompletedModule + 1) {
            // В следующем модуле - только если это первый урок и последний урок предыдущего модуля завершен
            const lastLessonIdInPrevModule = getLastLessonIdInModule(moduleIdNum - 1);

            if (currentLessonIndex === 0 && lastCompletedLessonId === lastLessonIdInPrevModule) {
                return { disabled: false, message: 'Далее', reason: '' };
            }
            return { disabled: true, message: 'Далее', reason: 'completion' };
        }
        else {
            // Модули после следующего - все заблокированы
            return { disabled: true, message: 'Далее', reason: 'completion' };
        }
    };

    // Проверка возможности перехода к следующему уроку
    const canNavigateToNextLesson = () => {
        // Если это последний урок в модуле
        if (currentLessonIndex >= currentModuleLessons.length - 1) {
            return false;
        }

        // Администраторам доступны все уроки
        if (adminMode) {
            return true;
        }

        // Проверяем блокировку следующего урока из БД
        const nextLesson = currentModuleLessons[currentLessonIndex + 1];
        if (nextLesson) {
            const moduleIdNum = Number(moduleName);
            const nextLessonIdNum = Number(nextLesson.id);
            const accessInfo = lessonAccess.find(
                access => access.moduleId === moduleIdNum && access.lessonId === nextLessonIdNum
            );

            if (accessInfo && accessInfo.isLocked) {
                return false;
            }
        }

        const nextButtonStatus = getNextButtonStatus();
        return !nextButtonStatus.disabled;
    };

    // Функция для сохранения новой ссылки на видео
    const saveVideoUrl = async () => {
        setIsSaving(true);
        setErrorMessage('');

        try {
            const response = await axiosInstance.post(`/lesson-videos/${moduleName}/${lessonId}`, {
                videoUrl: newVideoUrl.trim()
            });

            if (response.data.success) {
                setVideoUrl(newVideoUrl.trim());
                setIsEditingUrl(false);
            } else {
                setErrorMessage(response.data.message || 'Не удалось сохранить ссылку на видео');
            }
            setIsSaving(false);
        } catch (error) {
            setErrorMessage(error.response?.data?.error || 'Не удалось сохранить ссылку на видео');
            setIsSaving(false);
        }
    };

    // Функция для отмены редактирования
    const cancelEditing = () => {
        setNewVideoUrl(videoUrl);
        setIsEditingUrl(false);
        setErrorMessage('');
    };

    // Функция для начала редактирования
    const startEditing = () => {
        setIsEditingUrl(true);
        setNewVideoUrl(videoUrl);
        // Устанавливаем фокус на поле ввода после рендеринга
        setTimeout(() => {
            if (videoInputRef.current) {
                videoInputRef.current.focus();
            }
        }, 0);
    };

    // Функция для возврата на главную страницу с выбранным модулем
    const handleBackClick = () => {
        // Возвращаемся на главную и устанавливаем выбранный модуль через state
        navigate('/', {
            state: {
                selectedModule: Number(moduleName) || moduleName
            }
        });
    };

    const updateLessonTitle = useCallback((title) => {
        if (title !== lessonTitle) {
            setLessonTitle(title);
        }
    }, [lessonTitle]);

    // Получаем ID видео из ссылки YouTube
    const getYoutubeVideoId = (url) => {
        if (!url) return null;

        // Паттерны для разных форматов ссылок YouTube
        const patterns = [
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/i,
            /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/i,
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/i
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    };

    // Преобразуем ссылку в формат для embed
    const getEmbedUrl = (url) => {
        if (!url) return '';

        // YouTube
        const videoId = getYoutubeVideoId(url);
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
        }

        // VK
        if (url.includes('vk.com') || url.includes('vkvideo.ru')) {
            // Пробуем разные паттерны для VK ссылок
            let vkVideoId = url.match(/video(-?\d+_\d+)/)?.[1];

            // Если не нашли, пробуем паттерн без "video" в начале
            if (!vkVideoId) {
                vkVideoId = url.match(/(-?\d+_\d+)/)?.[1];
            }

            if (vkVideoId) {
                const oid = vkVideoId.split('_')[0];
                const id = vkVideoId.split('_')[1];

                // Попробуем наиболее совместимый формат для VK
                // Убираем hd=1 так как это может вызывать проблемы с некоторыми видео
                return `https://vk.com/video_ext.php?oid=${oid}&id=${id}`;
            }
        }

        // Google Drive - используем встраиваемую ссылку
        if (url.includes('drive.google.com')) {
            let fileId = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/)?.[1];

            // Также проверяем формат открытого доступа
            if (!fileId) {
                fileId = url.match(/\/open\?id=([a-zA-Z0-9-_]+)/)?.[1];
            }

            if (fileId) {
                // Используем embed ссылку Google Drive
                return `https://drive.google.com/file/d/${fileId}/preview`;
            }
        }

        // Яндекс.Диск - обрабатывается отдельно в renderVideo
        if (url.includes('disk.yandex.ru') || url.includes('yadi.sk')) {
            return url; // Возвращаем оригинальный URL, обработка происходит в renderVideo
        }

        // Rutube
        if (url.includes('rutube.ru')) {
            return url;
        }

        // Прямые ссылки на видео файлы (добавляем поддержку .mov)
        if (/\.(mp4|webm|mov|avi|mkv)$/i.test(url)) {
            return url;
        }

        return url; // Если формат не распознан, возвращаем как есть
    };

    // Функция для определения типа ресурса
    const getResourceType = (url) => {
        if (!url) return null;

        const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
        const isVK = url.includes('vk.com') || url.includes('vkvideo.ru');
        const isRutube = url.includes('rutube.ru');
        const isGoogleDrive = url.includes('drive.google.com');
        const isYandexDisk = url.includes('disk.yandex.ru') || url.includes('yadi.sk');
        const isGetCourse = url.includes('getcourse.ru') || url.includes('getcourse.io') || url.includes('fs.getcourse.ru');

        // GetCourse проверяем ПЕРЕД проверкой прямых файлов, 
        // так как fs.getcourse.ru файлы требуют особой обработки
        if (isGetCourse) return 'getcourse';

        const isDirectVideo = /\.(mp4|webm|mov|avi|mkv|m4v|3gp|flv)$/i.test(url) && !isGetCourse;
        const isDirectAudio = /\.(mp3|wav|ogg|aac|m4a|flac)$/i.test(url) && !isGetCourse;
        const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) && !isGetCourse;

        if (isDirectVideo) return 'video';
        if (isDirectAudio) return 'audio';
        if (isImage) return 'image';
        if (isYoutube) return 'youtube';
        if (isVK) return 'vk';
        if (isRutube) return 'rutube';
        if (isGoogleDrive) return 'google-drive';
        if (isYandexDisk) return 'yandex-disk';

        return 'iframe'; // По умолчанию пробуем iframe
    };

    // Функция для рендеринга прямого видео файла
    const renderDirectVideo = (url) => {
        const getVideoType = (url) => {
            if (url.endsWith('.mp4')) return 'video/mp4';
            if (url.endsWith('.webm')) return 'video/webm';
            if (url.endsWith('.mov')) return 'video/quicktime';
            if (url.endsWith('.avi')) return 'video/x-msvideo';
            if (url.endsWith('.mkv')) return 'video/x-matroska';
            if (url.endsWith('.m4v')) return 'video/mp4';
            if (url.endsWith('.3gp')) return 'video/3gpp';
            if (url.endsWith('.flv')) return 'video/x-flv';
            return 'video/mp4'; // По умолчанию
        };

        return (
            <div className="video-container">
                <video
                    controls
                    preload="metadata"
                    style={{ width: '100%', height: '100%' }}
                    onError={(e) => {
                        // Ошибка загрузки видео
                    }}
                >
                    <source src={url} type={getVideoType(url)} />
                    Ваш браузер не поддерживает воспроизведение видео.
                </video>
            </div>
        );
    };

    // Функция для рендеринга GetCourse видео (упрощенная, как в DetailPage)
    const renderGetCourseVideo = (url) => {
        // Если это файловый сервис GetCourse с видео файлом
        if (url.includes('fs.getcourse.ru')) {
            const isVideo = /\.(mp4|webm|mov|avi|mkv|m4v|3gp|flv)$/i.test(url);

            // Функция для определения правильного MIME-типа
            const getVideoType = (url) => {
                if (url.endsWith('.mp4') || url.includes('.mp4')) return 'video/mp4';
                if (url.endsWith('.webm') || url.includes('.webm')) return 'video/webm';
                if (url.endsWith('.mov') || url.includes('.mov')) return 'video/quicktime';
                if (url.endsWith('.avi') || url.includes('.avi')) return 'video/x-msvideo';
                if (url.endsWith('.mkv') || url.includes('.mkv')) return 'video/x-matroska';
                if (url.endsWith('.m4v') || url.includes('.m4v')) return 'video/mp4';
                if (url.endsWith('.3gp') || url.includes('.3gp')) return 'video/3gpp';
                if (url.endsWith('.flv') || url.includes('.flv')) return 'video/x-flv';
                return 'video/mp4'; // По умолчанию
            };

            if (isVideo) {
                // HTML5 video элемент
                return (
                    <div className="video-container">
                        <video
                            controls
                            preload="metadata"
                            crossOrigin="anonymous"
                            style={{ width: '100%', height: '100%' }}
                            onError={(e) => {
                                // Ошибка загрузки видео
                            }}
                        >
                            {/* Для .mov файлов пробуем несколько MIME-типов */}
                            {url.includes('.mov') ? (
                                <>
                                    <source src={url} type="video/quicktime" />
                                    <source src={url} type="video/mp4" />
                                    <source src={url} type="" />
                                </>
                            ) : (
                                <source src={url} type={getVideoType(url)} />
                            )}
                            Ваш браузер не поддерживает воспроизведение видео.
                        </video>
                    </div>
                );
            }
        }

        // GetCourse может использовать разные форматы embed ссылок для обычных уроков
        let embedUrl = url;

        // Если это обычная ссылка на урок, пробуем преобразовать в embed
        if (url.includes('/teach/control/lesson/view')) {
            // Пример: https://myschool.getcourse.ru/teach/control/lesson/view?id=123456789
            const lessonId = url.match(/id=(\d+)/)?.[1];
            const domain = url.match(/https?:\/\/([^/]+)/)?.[1];
            if (lessonId && domain) {
                embedUrl = `https://${domain}/pl/embed/lesson/${lessonId}`;
            }
        }

        return (
            <iframe
                src={embedUrl}
                title="GetCourse video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-forms"
                loading="lazy"
                style={{ border: 'none', width: '100%', height: '100%' }}
                referrerPolicy="no-referrer-when-downgrade"
                onError={(e) => {
                    // Ошибка загрузки iframe
                }}
            ></iframe>
        );
    };

    // Функция для рендеринга видео с поддержкой разных платформ
    const renderVideo = () => {
        if (!videoUrl) return null;

        const resourceType = getResourceType(videoUrl);

        switch (resourceType) {
            case 'video':
                return renderDirectVideo(videoUrl);

            case 'audio':
                return (
                    <div className="audio-container">
                        <audio
                            controls
                            style={{ width: '100%' }}
                            onError={(e) => {
                                // Ошибка загрузки аудио
                            }}
                        >
                            <source src={videoUrl} />
                            Ваш браузер не поддерживает воспроизведение аудио.
                        </audio>
                    </div>
                );

            case 'image':
                return (
                    <div className="image-container">
                        <img
                            src={videoUrl}
                            alt="Изображение урока"
                            style={{ maxWidth: '100%', height: 'auto' }}
                            onError={(e) => {
                                // Ошибка загрузки изображения
                            }}
                        />
                    </div>
                );

            case 'getcourse':
                return renderGetCourseVideo(videoUrl);

            case 'yandex-disk':
                // Специальная обработка для Яндекс.Диска
                let viewUrl = videoUrl;
                if (videoUrl.includes('/i/')) {
                    viewUrl = videoUrl;
                } else if (videoUrl.includes('/d/')) {
                    viewUrl = videoUrl.replace('/d/', '/i/');
                }

                return (
                    <iframe
                        src={viewUrl}
                        title="Яндекс.Диск video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-forms"
                        loading="lazy"
                        style={{ border: 'none', width: '100%', height: '100%' }}
                        referrerPolicy="no-referrer-when-downgrade"
                        onError={(e) => {
                            // Ошибка загрузки iframe
                        }}
                    ></iframe>
                );

            case 'youtube':
            case 'vk':
            case 'rutube':
            case 'google-drive':
            default:
                // Для остальных сервисов используем iframe с обработанной ссылкой
                const embedUrl = getEmbedUrl(videoUrl);
                let title = "Видео урока";

                if (resourceType === 'youtube') title = "YouTube video player";
                else if (resourceType === 'vk') title = "VK video player";
                else if (resourceType === 'rutube') title = "Rutube video player";
                else if (resourceType === 'google-drive') title = "Google Drive video player";

                return (
                    <iframe
                        src={embedUrl}
                        title={title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-forms allow-downloads"
                        loading="lazy"
                        style={{ border: 'none', width: '100%', height: '100%' }}
                        referrerPolicy="no-referrer-when-downgrade"
                        onError={(e) => {
                            // Ошибка загрузки iframe
                        }}
                    ></iframe>
                );
        }
    };

    // Рендерим заголовок с анимацией
    const renderAnimatedTitle = () => {
        // Если анимация заголовка активна (только для кнопок "Назад" и "Далее")
        if (titleAnimating) {
            return (
                <div className="title-animation-container">
                    {titleSlideDirection === 'exit' &&
                        <div className="title-slide exit">{previousTitle}</div>
                    }
                    {titleSlideDirection === 'enter' &&
                        <div className="title-slide enter">{lessonTitle}</div>
                    }
                    {!titleSlideDirection &&
                        <div className="title-slide">{lessonTitle}</div>
                    }
                </div>
            );
        }

        // Обычный режим без анимации
        return <span className="lesson-title">{lessonTitle}</span>;
    };

    // Рендерим счетчик уроков с анимацией
    const renderAnimatedCounter = () => {
        if (counterAnimation && prevCounterValue !== null) {
            // Анимация выхода текущего значения и входа нового значения с противоположной стороны
            return (
                <>
                    <div className={`lesson-counter-number exit ${counterAnimation}`}>
                        {prevCounterValue}
                    </div>
                    {slideDirection === 'enter' && (
                        <div className={`lesson-counter-number enter ${counterAnimation}`}>
                            {nextCounterValue}
                        </div>
                    )}
                </>
            );
        }

        // Обычный режим без анимации
        return (
            <div className="lesson-counter-number">
                {currentLessonIndex + 1}
            </div>
        );
    };

    return (
        <div className="lesson-content-container">
            <div className="lesson-content-header">
                <div className="lesson-title-container">
                    <button className="back-button" onClick={handleBackClick}>
                        <img src={backIcon} alt="Назад" />
                        {renderAnimatedTitle()}
                    </button>
                </div>
            </div>

            <div className="lesson-content-main">
                <div className="lesson-video-wrapper">
                    <div className="lesson-video-container">
                        {videoUrl ? (
                            <div className="video-wrapper" key={`${moduleName}-${lessonId}-${videoUrl}`}>
                                {renderVideo()}
                            </div>
                        ) : (
                            <div className="lesson-video-placeholder" key={`${moduleName}-${lessonId}-placeholder`}>
                                <div className="play-button">
                                    <span>▶</span>
                                </div>
                                <div className="video-placeholder-text">
                                    Ресурс не добавлен
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Навигационные кнопки для уроков и админ-контроли перемещены на один уровень */}
                    <div className="video-controls-container">
                        <div className="lesson-navigation">
                            <button
                                className={`nav-button prev-lesson ${!canNavigateToPreviousLesson() ? 'disabled' : ''}`}
                                onClick={goToPreviousLesson}
                                disabled={!canNavigateToPreviousLesson() || animating}
                            >
                                Назад
                            </button>
                            <div className="lesson-counter">
                                {renderAnimatedCounter()}
                                <span> / {currentModuleLessons.length}</span>
                            </div>
                            {(() => {
                                const nextButtonStatus = getNextButtonStatus();
                                return (
                                    <button
                                        className={`nav-button next-lesson ${nextButtonStatus.disabled ? 'disabled' : ''} ${nextButtonStatus.reason ? `reason-${nextButtonStatus.reason}` : ''}`}
                                        onClick={goToNextLesson}
                                        disabled={nextButtonStatus.disabled || animating}
                                        title={nextButtonStatus.disabled ? nextButtonStatus.message : ''}
                                    >
                                        {nextButtonStatus.message}
                                    </button>
                                );
                            })()}
                        </div>

                        {adminMode && (
                            <div className="video-admin-controls">
                                {isEditingUrl ? (
                                    <div className="video-url-form">
                                        <input
                                            ref={videoInputRef}
                                            type="text"
                                            value={newVideoUrl}
                                            onChange={(e) => setNewVideoUrl(e.target.value)}
                                            placeholder="Введите ссылку на ресурс (YouTube, VK, Rutube, Google Drive, Яндекс.Диск, GetCourse файлы, прямые ссылки на видео/аудио/изображения)"
                                            className="video-url-input"
                                        />
                                        <div className="video-url-actions">
                                            <button
                                                onClick={saveVideoUrl}
                                                disabled={isSaving}
                                                className="video-url-save-btn"
                                            >
                                                {isSaving ? 'Сохранение...' : 'Сохранить'}
                                            </button>
                                            <button
                                                onClick={cancelEditing}
                                                className="video-url-cancel-btn"
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                        {errorMessage && (
                                            <div className="video-url-error">{errorMessage}</div>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        onClick={startEditing}
                                        className="video-url-edit-btn"
                                    >
                                        {videoUrl ? 'Изменить ссылку на ресурс' : 'Добавить ресурс'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="lesson-tabs">
                    <button
                        className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => handleTabChange('description')}
                        disabled={tabAnimating}
                    >
                        Описание урока
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'homework' ? 'active' : ''}`}
                        onClick={() => handleTabChange('homework')}
                        disabled={tabAnimating}
                    >
                        Сдача ДЗ
                    </button>
                </div>

                <div className={`lesson-content-body ${slideDirection === 'enter' ? 'slide-enter' : slideDirection === 'exit' ? 'slide-exit' : ''}`}>
                    {/* Рендеринг контента с предыдущей вкладкой во время анимации */}
                    {tabAnimating && slideDirection === 'exit' && previousTab === 'description' && (
                        <LessonDescription
                            moduleName={moduleName}
                            lessonId={lessonId}
                            updateLessonTitle={updateLessonTitle}
                        />
                    )}
                    {tabAnimating && slideDirection === 'exit' && previousTab === 'homework' && (
                        <LessonHomework
                            moduleName={moduleName}
                            lessonId={lessonId}
                            updateLessonTitle={updateLessonTitle}
                        />
                    )}

                    {/* Рендеринг контента с новой вкладкой */}
                    {(!tabAnimating || slideDirection === 'enter') && activeTab === 'description' && (
                        <LessonDescription
                            moduleName={moduleName}
                            lessonId={lessonId}
                            updateLessonTitle={updateLessonTitle}
                        />
                    )}
                    {(!tabAnimating || slideDirection === 'enter') && activeTab === 'homework' && (
                        <LessonHomework
                            moduleName={moduleName}
                            lessonId={lessonId}
                            updateLessonTitle={updateLessonTitle}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LessonContent; 